import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import { useSerialStore } from '../stores/serialStore';
import { serialService } from '../services/serialService';
import type { DebugDataPoint } from '../stores/debugStore';

// Regex to match: DEBUG> 1.234 5.678 9.012 ... (9 floats) followed by 'r'
const DEBUG_REGEX = /DEBUG>\s*([+-]?\d+(?:\.\d+)?)\s+([+-]?\d+(?:\.\d+)?)\s+([+-]?\d+(?:\.\d+)?)\s+([+-]?\d+(?:\.\d+)?)\s+([+-]?\d+(?:\.\d+)?)\s+([+-]?\d+(?:\.\d+)?)\s+([+-]?\d+(?:\.\d+)?)\s+([+-]?\d+(?:\.\d+)?)\s+([+-]?\d+(?:\.\d+)?)/;

// Helper functions to convert data to uPlot format with 60s window padding
const WINDOW_DURATION_MS = 60000; // 60 seconds in milliseconds

const convertTempDataToUplot = (data: DebugDataPoint[]): uPlot.AlignedData => {
  const timestamps: number[] = [];
  const peltierTempFiltered: (number | null)[] = [];
  const peltierTempTarget: (number | null)[] = [];
  const lidTempFiltered: (number | null)[] = [];
  const lidTempTarget: (number | null)[] = [];

  if (data.length === 0) {
    return [[], [], [], [], []];
  }

  const firstTimestamp = data[0].time;
  const lastTimestamp = data[data.length - 1].time;
  const dataSpan = lastTimestamp - firstTimestamp;

  // Add actual data, treating -1 as null for target values
  for (const point of data) {
    timestamps.push(point.time);
    peltierTempFiltered.push(point.peltierTemperatureFiltered);
    peltierTempTarget.push(point.peltierTemperatureTarget === -1 ? null : point.peltierTemperatureTarget);
    lidTempFiltered.push(point.lidTemperatureFiltered);
    lidTempTarget.push(point.lidTemperatureTarget === -1 ? null : point.lidTemperatureTarget);
  }

  // If the data span is less than 60 seconds, add one padding point at 60s mark
  if (dataSpan < WINDOW_DURATION_MS) {
    timestamps.push(firstTimestamp + WINDOW_DURATION_MS);
    peltierTempFiltered.push(null);
    peltierTempTarget.push(null);
    lidTempFiltered.push(null);
    lidTempTarget.push(null);
  }

  return [timestamps, peltierTempFiltered, peltierTempTarget, lidTempFiltered, lidTempTarget];
};

const convertCurrentDataToUplot = (data: DebugDataPoint[]): uPlot.AlignedData => {
  const timestamps: number[] = [];
  const peltierCurrentFiltered: (number | null)[] = [];
  const peltierCurrentTarget: (number | null)[] = [];
  const lidCurrentFiltered: (number | null)[] = [];

  if (data.length === 0) {
    return [[], [], [], []];
  }

  const firstTimestamp = data[0].time;
  const lastTimestamp = data[data.length - 1].time;
  const dataSpan = lastTimestamp - firstTimestamp;

  // Add actual data, treating -1 as null for target values
  for (const point of data) {
    timestamps.push(point.time);
    peltierCurrentFiltered.push(point.peltierCurrentFiltered);
    peltierCurrentTarget.push(point.peltierCurrentTarget === -1 ? null : point.peltierCurrentTarget);
    lidCurrentFiltered.push(point.lidCurrentFiltered);
  }

  // If the data span is less than 60 seconds, add one padding point at 60s mark
  if (dataSpan < WINDOW_DURATION_MS) {
    timestamps.push(firstTimestamp + WINDOW_DURATION_MS);
    peltierCurrentFiltered.push(null);
    peltierCurrentTarget.push(null);
    lidCurrentFiltered.push(null);
  }

  return [timestamps, peltierCurrentFiltered, peltierCurrentTarget, lidCurrentFiltered];
};

const convertVoltageDataToUplot = (data: DebugDataPoint[]): uPlot.AlignedData => {
  const timestamps: number[] = [];
  const voltageTarget: (number | null)[] = [];
  const dutyCycle: (number | null)[] = [];

  if (data.length === 0) {
    return [[], [], []];
  }

  const firstTimestamp = data[0].time;
  const lastTimestamp = data[data.length - 1].time;
  const dataSpan = lastTimestamp - firstTimestamp;

  // Add actual data
  for (const point of data) {
    timestamps.push(point.time);
    voltageTarget.push(point.hbridgeVoltageTarget);
    dutyCycle.push(point.hbridgeDutyCycle);
  }

  // If the data span is less than 60 seconds, add one padding point at 60s mark
  if (dataSpan < WINDOW_DURATION_MS) {
    timestamps.push(firstTimestamp + WINDOW_DURATION_MS);
    voltageTarget.push(null);
    dutyCycle.push(null);
  }

  return [timestamps, voltageTarget, dutyCycle];
};

export const HardwareMonitorPlot: React.FC = () => {
  const [data, setData] = useState<DebugDataPoint[]>([]);
  const isConnected = useSerialStore((state) => state.isConnected);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const bufferRef = useRef<string>('');
  const tempContainerRef = useRef<HTMLDivElement>(null);
  const currentContainerRef = useRef<HTMLDivElement>(null);
  const voltageContainerRef = useRef<HTMLDivElement>(null);
  const tempPlotRef = useRef<uPlot | null>(null);
  const currentPlotRef = useRef<uPlot | null>(null);
  const voltagePlotRef = useRef<uPlot | null>(null);
  const [plotSize, setPlotSize] = useState({ width: 800, height: 250 });
  const updateTimerRef = useRef<number | null>(null);
  const lastUpdateRef = useRef<number>(0);

  useEffect(() => {
    if (!isConnected) {
      return;
    }

    const handleSerialData = (chunk: string) => {
      const arrivalTimestamp = Date.now();
      bufferRef.current += chunk;

      const lines = bufferRef.current.split('\n');
      bufferRef.current = lines.pop() || '';

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        const debugMatch = trimmedLine.match(DEBUG_REGEX);

        if (debugMatch) {
          try {
            const values = [
              parseFloat(debugMatch[1]), // peltierCurrentFiltered
              parseFloat(debugMatch[2]), // peltierTemperatureFiltered
              parseFloat(debugMatch[3]), // peltierTemperatureTarget
              parseFloat(debugMatch[4]), // peltierCurrentTarget
              parseFloat(debugMatch[5]), // lidCurrentFiltered
              parseFloat(debugMatch[6]), // lidTemperatureFiltered
              parseFloat(debugMatch[7]), // lidTemperatureTarget
              parseFloat(debugMatch[8]), // hbridgeVoltageTarget
              parseFloat(debugMatch[9]), // hbridgeDutyCycle
            ];

            // Validate all values are numbers
            if (values.every((v) => !isNaN(v))) {
              const newPoint: DebugDataPoint = {
                time: arrivalTimestamp,
                peltierCurrentFiltered: values[0],
                peltierTemperatureFiltered: values[1],
                peltierTemperatureTarget: values[2],
                peltierCurrentTarget: values[3],
                lidCurrentFiltered: values[4],
                lidTemperatureFiltered: values[5],
                lidTemperatureTarget: values[6],
                hbridgeVoltageTarget: values[7],
                hbridgeDutyCycle: values[8],
              };

              setData((prevData) => {
                const newData = [...prevData, newPoint];
                // Keep only last 600 data points
                if (newData.length > 600) {
                  return newData.slice(-600);
                }
                return newData;
              });
            }
          } catch (error) {
            console.error('Error parsing debug data:', error);
          }
        }
      }
    };

    const unsubscribe = serialService.onData(handleSerialData);

    return () => {
      unsubscribe();
      bufferRef.current = '';
    };
  }, [isConnected]);

  // Convert data to uPlot format for temperature chart
  const tempUplotData = useMemo((): uPlot.AlignedData => convertTempDataToUplot(data), [data]);

  // Convert data to uPlot format for current chart
  const currentUplotData = useMemo((): uPlot.AlignedData => convertCurrentDataToUplot(data), [data]);

  // Convert data to uPlot format for voltage chart
  const voltageUplotData = useMemo((): uPlot.AlignedData => convertVoltageDataToUplot(data), [data]);

  // Calculate ranges
  const tempRange = useMemo((): uPlot.Range.MinMax => {
    if (data.length === 0) return [0, 100];

    let min = Infinity;
    let max = -Infinity;

    for (const point of data) {
      const values = [
        point.peltierTemperatureFiltered,
        point.peltierTemperatureTarget,
        point.lidTemperatureFiltered,
        point.lidTemperatureTarget,
      ];
      for (const v of values) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }

    return [min - 5, max + 5];
  }, [data]);

  const currentRange = useMemo((): uPlot.Range.MinMax => {
    if (data.length === 0) return [0, 10];

    let min = Infinity;
    let max = -Infinity;

    for (const point of data) {
      const values = [
        point.peltierCurrentFiltered,
        point.peltierCurrentTarget,
        point.lidCurrentFiltered,
      ];
      for (const v of values) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }

    return [min - 0.5, max + 0.5];
  }, [data]);

  const voltageRange = useMemo((): uPlot.Range.MinMax => {
    if (data.length === 0) return [0, 100];

    let min = Infinity;
    let max = -Infinity;

    for (const point of data) {
      const values = [point.hbridgeVoltageTarget, point.hbridgeDutyCycle];
      for (const v of values) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }

    return [min - 1, max + 1];
  }, [data]);

  // Initialize temperature plot
  useEffect(() => {
    if (!tempContainerRef.current) return;

    const width = tempContainerRef.current.clientWidth;
    const height = tempContainerRef.current.clientHeight;
    if (width === 0 || height === 0) {
      // Tab not visible yet - clean up any existing plot
      if (tempPlotRef.current) {
        tempPlotRef.current.destroy();
        tempPlotRef.current = null;
      }
      return;
    }

    // If plot already exists with correct theme, don't recreate
    if (tempPlotRef.current) return;

    // Get current data at initialization time using helper function
    const initialData: uPlot.AlignedData = convertTempDataToUplot(data);

    // Calculate initial tempRange
    let min = Infinity;
    let max = -Infinity;
    for (const point of data) {
      const values = [point.peltierTemperatureFiltered, point.peltierTemperatureTarget, point.lidTemperatureFiltered, point.lidTemperatureTarget];
      for (const val of values) {
        if (val < min) min = val;
        if (val > max) max = val;
      }
    }
    const initialTempRange: uPlot.Range.MinMax = data.length > 0 ? [min - 2, max + 2] : [0, 100];

    const opts: uPlot.Options = {
      width,
      height: height - 30,
      series: [
        {
          value: (_u, v) => {
            if (v == null) return '--';
            const date = new Date(v);
            return date.toLocaleString();
          },
        },
        {
          label: 'Peltier Temp',
          stroke: isDark ? '#42a5f5' : '#2196f3',
          width: 2,
          points: { show: false },
          value: (_u, v) => v == null ? '--' : v.toFixed(2) + '°C',
        },
        {
          label: 'Peltier Target',
          stroke: isDark ? '#90caf9' : '#64b5f6',
          width: 2,
          points: { show: false },
          dash: [5, 5],
          value: (_u, v) => v == null ? '--' : v.toFixed(2) + '°C',
        },
        {
          label: 'Lid Temp',
          stroke: isDark ? '#ef5350' : '#f44336',
          width: 2,
          points: { show: false },
          value: (_u, v) => v == null ? '--' : v.toFixed(2) + '°C',
        },
        {
          label: 'Lid Target',
          stroke: isDark ? '#ff7961' : '#ef5350',
          width: 2,
          points: { show: false },
          dash: [5, 5],
          value: (_u, v) => v == null ? '--' : v.toFixed(2) + '°C',
        },
      ],
      axes: [
        {
          label: 'Time',
          labelSize: 18,
          labelGap: 0,
          stroke: isDark ? '#fff' : '#000',
          labelFont: `18px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`,
          grid: { stroke: isDark ? '#444' : '#e0e0e0', width: 1 },
          values: (_u, vals) => vals.map(v => {
            const date = new Date(v);
            return date.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
          }),
        },
        {
          label: 'Temperature (°C)',
          labelSize: 40,
          labelGap: 10,
          stroke: isDark ? '#fff' : '#000',
          labelFont: `14px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`,
          values: (_u, vals) => vals.map(v => v.toFixed(2) + '°C'),
          grid: { stroke: isDark ? '#444' : '#e0e0e0', width: 1 },
        },
      ],
      scales: {
        x: {},
        y: { range: () => initialTempRange },
      },
      cursor: {
        drag: { x: false, y: false },
        points: {
          size: 8,
          width: 2,
          stroke: isDark ? '#fff' : '#000',
          fill: isDark ? '#333' : '#fff',
        },
      },
      legend: { show: true, live: true },
    };

    tempPlotRef.current = new uPlot(opts, initialData, tempContainerRef.current);

    return () => {
      tempPlotRef.current?.destroy();
      tempPlotRef.current = null;
    };
  }, [plotSize, isDark]);

  // Initialize current plot
  useEffect(() => {
    if (!currentContainerRef.current) return;

    const width = currentContainerRef.current.clientWidth;
    const height = currentContainerRef.current.clientHeight;
    if (width === 0 || height === 0) {
      // Tab not visible yet - clean up any existing plot
      if (currentPlotRef.current) {
        currentPlotRef.current.destroy();
        currentPlotRef.current = null;
      }
      return;
    }

    // If plot already exists with correct theme, don't recreate
    if (currentPlotRef.current) return;

    // Get current data at initialization time using helper function
    const initialData: uPlot.AlignedData = convertCurrentDataToUplot(data);

    // Calculate initial currentRange
    let min = Infinity;
    let max = -Infinity;
    for (const point of data) {
      const values = [point.peltierCurrentFiltered, point.peltierCurrentTarget, point.lidCurrentFiltered];
      for (const val of values) {
        if (val < min) min = val;
        if (val > max) max = val;
      }
    }
    const initialCurrentRange: uPlot.Range.MinMax = data.length > 0 ? [min - 0.5, max + 0.5] : [0, 5];

    const opts: uPlot.Options = {
      width,
      height: height - 30,
      series: [
        {
          value: (_u, v) => {
            if (v == null) return '--';
            const date = new Date(v);
            return date.toLocaleString('en-US', {
              hour12: false,
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            });
          },
        },
        {
          label: 'Peltier Current',
          stroke: isDark ? '#42a5f5' : '#1976d2',
          width: 2,
          points: { show: false },
          value: (_u, v) => v == null ? '--' : v.toFixed(2) + 'A',
        },
        {
          label: 'Peltier Target',
          stroke: isDark ? '#90caf9' : '#64b5f6',
          width: 2,
          points: { show: false },
          dash: [5, 5],
          value: (_u, v) => v == null ? '--' : v.toFixed(2) + 'A',
        },
        {
          label: 'Lid Current',
          stroke: isDark ? '#ef5350' : '#d32f2f',
          width: 2,
          points: { show: false },
          value: (_u, v) => v == null ? '--' : v.toFixed(2) + 'A',
        },
      ],
      axes: [
        {
          label: 'Time',
          labelSize: 18,
          labelGap: 0,
          stroke: isDark ? '#fff' : '#000',
          labelFont: `18px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`,
          grid: { stroke: isDark ? '#444' : '#e0e0e0', width: 1 },
          values: (_u, vals) => vals.map(v => {
            const date = new Date(v);
            return date.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
          }),
        },
        {
          label: 'Current (A)',
          labelSize: 40,
          labelGap: 10,
          stroke: isDark ? '#fff' : '#000',
          labelFont: `14px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`,
          values: (_u, vals) => vals.map(v => v.toFixed(3) + 'A'),
          grid: { stroke: isDark ? '#444' : '#e0e0e0', width: 1 },
        },
      ],
      scales: {
        x: {},
        y: { range: () => initialCurrentRange },
      },
      cursor: {
        drag: { x: false, y: false },
        points: {
          size: 8,
          width: 2,
          stroke: isDark ? '#fff' : '#000',
          fill: isDark ? '#333' : '#fff',
        },
      },
      legend: { show: true, live: true },
    };

    currentPlotRef.current = new uPlot(opts, initialData, currentContainerRef.current);

    return () => {
      currentPlotRef.current?.destroy();
      currentPlotRef.current = null;
    };
  }, [plotSize, isDark]);

  // Initialize voltage plot
  useEffect(() => {
    if (!voltageContainerRef.current) return;

    const width = voltageContainerRef.current.clientWidth;
    const height = voltageContainerRef.current.clientHeight;
    if (width === 0 || height === 0) {
      // Tab not visible yet - clean up any existing plot
      if (voltagePlotRef.current) {
        voltagePlotRef.current.destroy();
        voltagePlotRef.current = null;
      }
      return;
    }

    // If plot already exists with correct theme, don't recreate
    if (voltagePlotRef.current) return;

    // Get current data at initialization time using helper function
    const initialData: uPlot.AlignedData = convertVoltageDataToUplot(data);

    // Calculate initial voltageRange
    let min = Infinity;
    let max = -Infinity;
    for (const point of data) {
      const values = [point.hbridgeVoltageTarget, point.hbridgeDutyCycle];
      for (const val of values) {
        if (val < min) min = val;
        if (val > max) max = val;
      }
    }
    const initialVoltageRange: uPlot.Range.MinMax = data.length > 0 ? [min - 1, max + 1] : [0, 100];

    const opts: uPlot.Options = {
      width,
      height: height - 30,
      series: [
        {
          value: (_u, v) => {
            if (v == null) return '--';
            const date = new Date(v);
            return date.toLocaleString('en-US', {
              hour12: false,
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            });
          },
        },
        { label: 'Voltage Target', stroke: isDark ? '#66bb6a' : '#388e3c', width: 2, points: { show: false } },
        { label: 'Duty Cycle', stroke: isDark ? '#81c784' : '#66bb6a', width: 2, points: { show: false } },
      ],
      axes: [
        {
          label: 'Time',
          labelSize: 18,
          labelGap: 0,
          stroke: isDark ? '#fff' : '#000',
          labelFont: `18px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`,
          grid: { stroke: isDark ? '#444' : '#e0e0e0', width: 1 },
          values: (_u, vals) => vals.map(v => {
            const date = new Date(v);
            return date.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
          }),
        },
        {
          label: 'Value',
          labelSize: 40,
          labelGap: 10,
          stroke: isDark ? '#fff' : '#000',
          labelFont: `14px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`,
          values: (_u, vals) => vals.map(v => v.toFixed(2)),
          grid: { stroke: isDark ? '#444' : '#e0e0e0', width: 1 },
        },
      ],
      scales: {
        x: {},
        y: { range: () => initialVoltageRange },
      },
      cursor: {
        drag: { x: false, y: false },
        points: {
          size: 8,
          width: 2,
          stroke: isDark ? '#fff' : '#000',
          fill: isDark ? '#333' : '#fff',
        },
      },
      legend: { show: true, live: true },
    };

    voltagePlotRef.current = new uPlot(opts, initialData, voltageContainerRef.current);

    return () => {
      voltagePlotRef.current?.destroy();
      voltagePlotRef.current = null;
    };
  }, [plotSize, isDark]);

  // Update all plots (throttled to max 30fps)
  useEffect(() => {
    if (!tempPlotRef.current || !currentPlotRef.current || !voltagePlotRef.current) return;

    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateRef.current;

    if (timeSinceLastUpdate < 33) {
      if (updateTimerRef.current) {
        window.clearTimeout(updateTimerRef.current);
      }

      updateTimerRef.current = window.setTimeout(() => {
        tempPlotRef.current?.setData(tempUplotData);
        currentPlotRef.current?.setData(currentUplotData);
        voltagePlotRef.current?.setData(voltageUplotData);
        lastUpdateRef.current = Date.now();
      }, 33 - timeSinceLastUpdate);

      return () => {
        if (updateTimerRef.current) {
          window.clearTimeout(updateTimerRef.current);
        }
      };
    }

    tempPlotRef.current.setData(tempUplotData);
    currentPlotRef.current.setData(currentUplotData);
    voltagePlotRef.current.setData(voltageUplotData);
    lastUpdateRef.current = now;
  }, [tempUplotData, currentUplotData, voltageUplotData]);

  // Update scales when ranges change
  useEffect(() => {
    if (tempPlotRef.current && tempRange[0] !== null && tempRange[1] !== null) {
      tempPlotRef.current.setScale('y', { min: tempRange[0], max: tempRange[1] });
    }
  }, [tempRange]);

  useEffect(() => {
    if (currentPlotRef.current && currentRange[0] !== null && currentRange[1] !== null) {
      currentPlotRef.current.setScale('y', { min: currentRange[0], max: currentRange[1] });
    }
  }, [currentRange]);

  useEffect(() => {
    if (voltagePlotRef.current && voltageRange[0] !== null && voltageRange[1] !== null) {
      voltagePlotRef.current.setScale('y', { min: voltageRange[0], max: voltageRange[1] });
    }
  }, [voltageRange]);

  // Handle resize for all plots
  useEffect(() => {
    const containers = [tempContainerRef.current, currentContainerRef.current, voltageContainerRef.current];
    const plots = [tempPlotRef, currentPlotRef, voltagePlotRef];

    if (!containers.every(c => c)) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        const newWidth = Math.floor(width);
        const newHeight = Math.floor((entry.contentRect.height || 250) - 30);

        if (newWidth > 0 && newHeight > 0) {
          setPlotSize({ width: newWidth, height: newHeight });
          plots.forEach(plotRef => {
            plotRef.current?.setSize({ width: newWidth, height: newHeight });
          });
        }
      }
    });

    containers.forEach(container => {
      if (container) resizeObserver.observe(container);
    });

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Box sx={{
      p: 2,
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      '& .u-legend': {
        color: theme.palette.text.primary,
      },
      '& .u-legend th': {
        color: theme.palette.text.primary,
      },
      '& .u-legend td': {
        color: theme.palette.text.primary,
      },
    }}>
      {/* Temperature Chart */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', fontSize: '11px' }}>
          Temperatures
        </Typography>
        <div ref={tempContainerRef} style={{ flexGrow: 1, minHeight: 0 }} />
      </Box>

      {/* Current Chart */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', fontSize: '11px' }}>
          Currents
        </Typography>
        <div ref={currentContainerRef} style={{ flexGrow: 1, minHeight: 0 }} />
      </Box>

      {/* Voltage & Duty Cycle Chart */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', fontSize: '11px' }}>
          H-Bridge Control
        </Typography>
        <div ref={voltageContainerRef} style={{ flexGrow: 1, minHeight: 0 }} />
      </Box>

      {!isConnected && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            bgcolor: 'background.paper',
            p: 2,
            borderRadius: 1,
          }}
        >
          <Typography color="text.secondary">
            Connect to serial port to view debug data
          </Typography>
        </Box>
      )}
    </Box>
  );
};
